import React, { useState, useEffect, useMemo, useRef } from "react";
import { ImportTool, ImportToolField } from "../types/import-tool";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { useToast } from "@/shared/components/ui/use-toast";
import { get, makeApiError } from "@/shared/utils/fetchApi";
import { Loader2 } from "lucide-react";
import { FileUpload } from "./FileUpload";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import { uploadKnowledgeFile } from "@/features/Knowledge/services/storageService";
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useKnowledge } from "@/features/Knowledge/hooks/useKnowledge";
import { useUpload } from "@/features/Knowledge/state/UploadContext";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { analyticsService } from "@/shared/services/analytics";
import { MagicFillButton } from "@/shared/components/form/MagicFillButton";
import { InputWithMagicFill } from "@/shared/components/form/InputWithMagicFill";
import { TextareaWithMagicFill } from "@/shared/components/form/TextareaWithMagicFill";
import { SelectWithMagicFill } from "@/shared/components/form/SelectWithMagicFill";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/ui/accordion";
import { getKnowledgeImportFormMetadata } from "@/shared/utils/magicFillMetadataHelpers";
import { MagicFillProvider } from "@/shared/contexts/MagicFillContext";
import { FacetSelector } from "@/features/Facets/components/FacetSelector";
import type { FacetFilter } from "@/features/Facets/types";
import { useFacets } from "@/features/Facets/hooks/useFacets";
import type { AvailableFacetForMagicFill } from "@/shared/types/magicFillTypes";

interface ImportToolFormProps {
  tool: ImportTool;
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImportToolForm({
  tool: initialTool,
  onSubmit,
  onCancel,
  open,
  onOpenChange,
}: ImportToolFormProps) {
  const { toast } = useToast();
  const { showUploadProgress, updateProgress, hideUploadProgress } =
    useUpload();
  const queryClient = useQueryClient();
  const [_activeTab, _setActiveTab] = useState("config");
  const [isLoading, setIsLoading] = useState(false);
  const [tool, setTool] = useState<ImportTool>(initialTool);
  const [toolFields, setToolFields] = useState<ImportToolField[]>([]);
  const { currentWorkspace } = useWorkspace();
  const { user: _user } = useAuth();
  const { data: knowledgeBase } = useKnowledge();
  // PBI 79319 — facetas disponíveis na organização (vocabulário) pra Magic Fill
  // restringir as sugestões ao vocabulário controlado. Lista cacheada via React
  // Query (ver useFacets) — o derive abaixo roda na memo, sem fetch por render.
  const { data: facetRows = [], isSuccess: facetsLoaded } = useFacets(
    currentWorkspace?.organization_id,
  );
  const magicFillButtonRef = useRef<{ triggerMagicFill: () => void }>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    schedule: {
      enabled: false,
      interval: "daily",
      time: "12:00",
      days: ["Segunda", "Quarta", "Sexta"],
    },
    fields: {} as Record<string, unknown>,
    // PBI 79328 — atributos de negócio livres do documento (source, subject,
    // language, tags, …). Vai como `metadata` no upload; persistido em
    // `storage.metadata` e replicado em embeddings.metadata.
    metadata: {} as Record<string, string>,
  });
  const { t } = useTranslation();

  // Referência para o formulário para manter o foco
  const nameInputRef = useRef<HTMLInputElement>(null);
  const descInputRef = useRef<HTMLTextAreaElement>(null);
  const [hasTriggeredMagicFill, setHasTriggeredMagicFill] = useState(false);

  useEffect(() => {
    if (initialTool?.id) {
      fetchToolDetails(initialTool.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTool?.id]);

  // PBI 79319 — facetas da org mapeadas pro Magic Fill (vocabulário controlado).
  // Derivado da lista cacheada; sem fetch por render.
  const availableFacetsForMF = useMemo<AvailableFacetForMagicFill[]>(() => {
    return facetRows.map((f) => {
      const labelKey = `facets.labels.values.${f.value}`;
      const translatedLabel = t(labelKey);
      const label = translatedLabel === labelKey ? undefined : translatedLabel;
      const parent = f.parent_facet_id
        ? facetRows.find((p) => p.id === f.parent_facet_id)
        : undefined;
      return {
        name: f.name,
        value: f.value,
        label,
        parent: parent ? { name: parent.name, value: parent.value } : null,
      };
    });
  }, [facetRows, t]);

  // Auto-trigger Magic Fill quando arquivo for carregado.
  // Aguarda facetsLoaded para garantir que o vocabulário de facetas está disponível
  // antes de disparar — sem isso a primeira tentativa envia availableFacets=[],
  // o LLM não recebe as instruções de facetas e elas são descartadas na normalização.
  useEffect(() => {
    const fileField = toolFields.find((f) => f.type === "file");
    if (
      fileField &&
      formData.fields[fileField.name] instanceof File &&
      !hasTriggeredMagicFill &&
      facetsLoaded
    ) {
      // O timer é cancelado na limpeza do efeito: as dependências incluem
      // `formData.name`/`description`, que mudam enquanto o usuário digita, e sem
      // isto cada re-execução enfileirava outro timer — o Magic Fill disparava
      // mais de uma vez para o mesmo arquivo.
      const timer = setTimeout(() => {
        magicFillButtonRef.current?.triggerMagicFill();
        setHasTriggeredMagicFill(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [
    formData.fields,
    toolFields,
    hasTriggeredMagicFill,
    facetsLoaded,
    formData.name,
    formData.description,
  ]);

  const fetchToolDetails = async (toolId: string) => {
    setIsLoading(true);
    try {
      // v2 e legacy retornam o node flat (sem envelope { data })
      const toolData = await get<ImportTool>(`/nodes/${toolId}`);

      setTool(toolData);

      // Extrair campos do settings
      const fields = toolData.settings?.fields || [];

      setToolFields(fields);

      // Inicializar valores padrão para os campos
      const initialFields: Record<string, unknown> = {};
      fields.forEach((field: ImportToolField) => {
        // Garantir que os valores padrão sejam definidos corretamente
        if (field.type === "select") {
          // Aplica o default do node quando houver (ex.: encoding=utf-8 no JSON/CSV
          // Import). Sem isso, selects required ficavam vazios e o usuário batia em
          // "campos obrigatórios" sem nunca ter aberto o campo (bug #82991).
          initialFields[field.name] = field.default ?? "";
        } else if (field.type === "boolean" && field.default !== undefined) {
          initialFields[field.name] =
            field.default === true || field.default === "true";
        } else {
          initialFields[field.name] = field.default || "";
        }
      });

      setFormData((prev) => ({
        ...prev,
        fields: initialFields,
      }));
    } catch (error) {
      console.error("Error fetching tool details:", error);
      toast({
        title: t("common.error"),
        description: t("knowledge.imports.toolLoadError"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldChange = (name: string, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToolFieldChange = (name: string, value: unknown) => {
    // Tratamento especial para campos select
    const field = toolFields.find((f) => f.name === name);
    if (field?.type === "select") {
      // Campo select
    } else if (field?.type === "file") {
      // Garantir que o arquivo seja tratado corretamente
      if (value instanceof File) {
        // Se já tinha preenchido com IA antes, limpar campos para o novo documento
        // (inclui facets — PBI 79319, mesmo comportamento dos outros campos
        // preenchidos pelo magic fill)
        if (hasTriggeredMagicFill) {
          setFormData((prev) => ({
            ...prev,
            name: "",
            description: "",
            fields: {
              ...prev.fields,
              [name]: value,
              facets: [],
            },
          }));
          setHasTriggeredMagicFill(false);
          return; // Retornar aqui para não executar o setFormData abaixo
        }
        // Reset flag quando novo arquivo é selecionado
        setHasTriggeredMagicFill(false);
      } else if (value === null) {
        // Se IA já preencheu, limpar campos ao remover arquivo
        if (hasTriggeredMagicFill) {
          setFormData((prev) => ({
            ...prev,
            name: "",
            description: "",
            fields: {
              ...prev.fields,
              [name]: null,
              facets: [],
            },
          }));
          setHasTriggeredMagicFill(false);
          return; // Retornar aqui para não executar o setFormData abaixo
        }
        setHasTriggeredMagicFill(false);
      }
    }

    setFormData((prev) => ({
      ...prev,
      fields: {
        ...prev.fields,
        [name]: value,
      },
    }));
  };

  const handleScheduleChange = (name: string, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [name]: value,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar campos obrigatórios
    const requiredFields = toolFields.filter((field) => field.required);
    const missingFields = requiredFields.filter((field) => {
      // Verificar se o campo é um arquivo
      if (field.type === "file") {
        return (
          !formData.fields[field.name] ||
          !(formData.fields[field.name] instanceof File)
        );
      }
      return !formData.fields[field.name];
    });

    if (!formData.name) {
      toast({
        title: t("knowledge.imports.validation.requiredField"),
        description: t("knowledge.imports.validation.requiredFieldDescription"),
        variant: "destructive",
      });
      return;
    }

    // PBI 79319 — exige pelo menos UMA faceta (qualquer dimensão). Sem isso,
    // o documento entraria como órfão e dependeria do backfill defensivo da
    // migração 24. Forçando o user a escolher, o chunk fica classificado
    // desde a ingestão.
    const submittedFacets = Array.isArray(formData.fields.facets)
      ? (formData.fields.facets as FacetFilter[])
      : [];
    if (submittedFacets.length === 0) {
      toast({
        title: t("facets.errors.atLeastOneRequiredTitle"),
        description: t("facets.errors.atLeastOneRequiredDescription"),
        variant: "destructive",
      });
      return;
    }

    if (missingFields.length > 0) {
      toast({
        title: t("knowledge.imports.validation.requiredFields"),
        description: t(
          "knowledge.imports.validation.requiredFieldsDescription",
          { fields: missingFields.map((f) => f.label).join(", ") },
        ),
        variant: "destructive",
      });
      return;
    }

    // Verificar se a ferramenta é da categoria "Files" e se há um arquivo para upload
    if (tool.category === "Files") {
      const fileField = toolFields.find((field) => field.type === "file");
      if (fileField && formData.fields[fileField.name] instanceof File) {
        const uploadStartTime = Date.now();

        try {
          // Obter o arquivo para upload
          const file = formData.fields[fileField.name] as File;

          // Validar requisitos antes de mostrar o progresso
          if (!currentWorkspace?.id || !currentWorkspace?.organization_id) {
            throw makeApiError("Product or organization not found", {
              code: "E_PRODUCT_NOT_FOUND",
            });
          }

          if (!knowledgeBase?.id) {
            throw makeApiError(
              "Active knowledge base not found for the current product",
              { code: "E_KNOWLEDGE_BASE_NOT_FOUND" },
            );
          }

          // Fechar o formulário antes de mostrar o progresso
          onOpenChange?.(false);

          // Pequeno delay para garantir que o formulário fechou
          await new Promise((resolve) => setTimeout(resolve, 100));

          // Mostrar o diálogo de progresso
          showUploadProgress(file);

          // Preparar as configurações personalizadas com os dados do formulário
          const config: Record<string, unknown> = {};
          Object.entries(formData.fields).forEach(([key, value]) => {
            config[key] = value;
          });

          // PBI 79328 — empacota os atributos de negócio. Filtra entradas vazias
          // pra não criar `{source:""}` no JSON. Tags vira array quando o usuário
          // separa por vírgula; backend persiste como está.
          const rawMeta = formData.metadata || {};
          const documentMetadata: Record<string, unknown> = {};
          for (const [k, v] of Object.entries(rawMeta)) {
            if (typeof v === "string" && v.trim().length > 0) {
              if (k === "tags") {
                documentMetadata.tags = v
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
              } else {
                documentMetadata[k] = v.trim();
              }
            }
          }
          // Facets ficam em formData.fields.facets (vêm do FacetSelector). No
          // workflow save_in_storage elas viajam em `storage.metadata.facets`;
          // pro upload manual replicamos o mesmo shape pra a aba Metadados
          // mostrar tudo no mesmo lugar e busca/filtro funcionar uniformemente.
          const submittedFacetsForMeta = Array.isArray(formData.fields.facets)
            ? (formData.fields.facets as FacetFilter[])
            : [];
          if (submittedFacetsForMeta.length > 0) {
            documentMetadata.facets = submittedFacetsForMeta.map((f) => ({
              name: String(f.name),
              value: String(f.value),
            }));
          }

          // Realizar o upload do arquivo
          const uploadResponse = await uploadKnowledgeFile({
            file,
            name: formData.name,
            description: formData.description || undefined,
            knowledgeBaseId: knowledgeBase.id,
            type: tool.type, // Usando o tipo do node/ferramenta
            category: tool.category,
            nodeId: tool.id, // ID do node/ferramenta
            toolLogoUrl: tool.logo_url, // URL do logo da ferramenta
            productId: currentWorkspace.id,
            config, // Configurações personalizadas do formulário
            metadata:
              Object.keys(documentMetadata).length > 0
                ? documentMetadata
                : undefined,
            onProgress: updateProgress,
          });

          // Atualiza o cache do React Query
          queryClient.setQueryData(
            ["knowledgeStorage", knowledgeBase?.id],
            (oldData: Record<string, unknown> | undefined) =>
              oldData && {
                ...oldData,
                total: (oldData.total as number) + 1,
                data: [uploadResponse, ...(oldData.data as unknown[])],
              },
          );

          // Invalidate para garantir sincronização
          await queryClient.invalidateQueries({
            queryKey: ["knowledgeStorage", knowledgeBase?.id],
          });

          // Track successful upload
          analyticsService.trackEvent("document_uploaded", {
            document_type: file.type || tool.name || "unknown",
            file_size_kb: Math.round(file.size / 1024),
            upload_duration_ms: Date.now() - uploadStartTime,
            notebook_id: knowledgeBase.id,
          });

          // Fechar o diálogo de progresso
          hideUploadProgress();

          // Chamar onSubmit
          onSubmit({
            ...formData,
            tool: tool.id,
            uploadResponse,
          });
        } catch (error) {
          console.error("Erro ao fazer upload do arquivo:", error);

          // Verificar se é erro de subscription
          const isSubscriptionError =
            error instanceof Error && error.message === "SUBSCRIPTION_REQUIRED";
          const errorMessage =
            error instanceof Error ? error.message : "Unknown error";

          // Obter o arquivo para tracking
          const fileField = toolFields.find((field) => field.type === "file");
          const file =
            fileField && formData.fields[fileField.name] instanceof File
              ? (formData.fields[fileField.name] as File)
              : null;

          // Mostrar o erro no toast
          toast({
            title: isSubscriptionError
              ? t("knowledge.errors.subscriptionRequired")
              : t("knowledge.errors.uploadFailed"),
            description: isSubscriptionError
              ? t("knowledge.errors.subscriptionRequiredDescription")
              : resolveApiErrorMessage(error, t),
            variant: "destructive",
          });

          // Track upload failure
          if (file && knowledgeBase?.id) {
            analyticsService.trackEvent("document_upload_failed", {
              document_type: file.type || tool.name || "unknown",
              file_size_kb: Math.round(file.size / 1024),
              upload_duration_ms: Date.now() - uploadStartTime,
              notebook_id: knowledgeBase.id,
              error_message: errorMessage,
            });
          }

          // Esconder o diálogo de progresso
          hideUploadProgress();
        }
        return;
      }
    }

    // Se não for um upload de arquivo, apenas enviar os dados
    onSubmit(formData);
    onOpenChange?.(false);
  };

  // Evitar que o evento de mudança de campo cause problemas de foco
  const handleInputChange = (
    name: string,
    value: unknown,
    fieldType: string = "regular",
  ) => {
    // Capturar o elemento ativo antes de atualizar o estado
    const activeElement = document.activeElement;

    // Salvar a posição do cursor se for um textarea
    let selectionStart = null;
    let selectionEnd = null;
    if (activeElement === descInputRef.current && descInputRef.current) {
      selectionStart = descInputRef.current.selectionStart;
      selectionEnd = descInputRef.current.selectionEnd;
    }

    if (fieldType === "tool") {
      handleToolFieldChange(name, value);
    } else if (fieldType === "schedule") {
      handleScheduleChange(name, value);
    } else {
      handleFieldChange(name, value);
    }

    // Usar requestAnimationFrame para garantir que o foco seja restaurado após a renderização
    requestAnimationFrame(() => {
      // Restaurar o foco e a posição do cursor para o textarea
      if (
        selectionStart !== null &&
        selectionEnd !== null &&
        descInputRef.current
      ) {
        descInputRef.current.focus();
        descInputRef.current.selectionStart = selectionStart;
        descInputRef.current.selectionEnd = selectionEnd;
      }
      // Verificar se o elemento ativo era o campo de nome
      else if (activeElement === nameInputRef.current) {
        nameInputRef.current?.focus();
      }
      // Outros elementos usando ID
      else if (activeElement) {
        const activeId = (activeElement as HTMLElement).id;
        if (activeId) {
          const elementToFocus = document.getElementById(activeId);
          if (elementToFocus) {
            elementToFocus.focus();
          }
        }
      }
    });
  };

  const getMagicFillMetadata = () => {
    // PBI 79319 — expõe o filename original como sinal pra IA. Sem isso,
    // o LLM só vê os campos textuais (name/description) que muitas vezes vêm
    // do title interno do PDF e podem não bater com o conteúdo real.
    const fileField = toolFields.find((f) => f.type === "file");
    const fileValue = fileField ? formData.fields[fileField.name] : undefined;
    const sourceFile =
      fileValue instanceof File
        ? {
            name: fileValue.name,
            type: fileValue.type,
            size: fileValue.size,
            file: fileValue,
          }
        : undefined;
    return {
      organizationId: currentWorkspace?.organization_id,
      workspaceId: currentWorkspace?.id,
      knowledgeBaseId: knowledgeBase?.id,
      fieldMetadata: getKnowledgeImportFormMetadata(availableFacetsForMF),
      ...(sourceFile ? { sourceFile } : {}),
    };
  };

  const getCurrentFormValues = () => ({
    name: formData.name,
    description: formData.description,
    ...(formData.fields || {}),
    // PBI 79328 — expõe metadata.* (prefixo `metadata.`) pro Magic Fill ler/escrever.
    ...Object.fromEntries(
      Object.entries(formData.metadata || {}).map(([k, v]) => [
        `metadata.${k}`,
        v,
      ]),
    ),
  });

  const handleMetadataChange = (key: string, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [key]: typeof value === "string" ? value : String(value ?? ""),
      },
    }));
  };

  const renderField = (field: ImportToolField) => {
    const rawValue = formData.fields[field.name];
    const value = rawValue as
      | string
      | number
      | boolean
      | File
      | null
      | undefined;

    switch (field.type) {
      case "text":
      case "number":
      case "password":
        return (
          <InputWithMagicFill
            formType="knowledge_import"
            fieldKey={field.name}
            getCurrentValues={getCurrentFormValues}
            onMagicFillApply={(val) =>
              handleInputChange(field.name, val, "tool")
            }
            metadata={getMagicFillMetadata()}
            type={field.type}
            id={field.name}
            name={field.name}
            value={
              typeof value === "string" || typeof value === "number"
                ? value
                : ""
            }
            onChange={(e) =>
              handleInputChange(field.name, e.target.value, "tool")
            }
            placeholder={field.placeholder}
            required={field.required}
            className="my-1"
          />
        );
      case "textarea":
        return (
          <TextareaWithMagicFill
            formType="knowledge_import"
            fieldKey={field.name}
            getCurrentValues={getCurrentFormValues}
            onMagicFillApply={(val) =>
              handleInputChange(field.name, val, "tool")
            }
            metadata={getMagicFillMetadata()}
            id={field.name}
            name={field.name}
            value={typeof value === "string" ? value : ""}
            onChange={(e) =>
              handleInputChange(field.name, e.target.value, "tool")
            }
            placeholder={field.placeholder}
            required={field.required}
            className="my-1"
          />
        );
      case "boolean":
        return (
          <div className="flex items-center space-x-2 my-1">
            <Checkbox
              id={field.name}
              checked={typeof value === "boolean" ? value : false}
              onCheckedChange={(checked) =>
                handleInputChange(field.name, checked, "tool")
              }
            />
            <Label htmlFor={field.name}>{field.label}</Label>
          </div>
        );
      case "select":
        return (
          <SelectWithMagicFill
            formType="knowledge_import"
            fieldKey={field.name}
            getCurrentValues={getCurrentFormValues}
            onMagicFillApply={(val) =>
              handleInputChange(field.name, val, "tool")
            }
            metadata={getMagicFillMetadata()}
            value={typeof value === "string" ? value : ""}
            onValueChange={(val) => {
              handleInputChange(field.name, val, "tool");
            }}
            options={field.options || []}
            placeholder={field.placeholder || "Selecione uma opção"}
            id={`select-${field.name}`}
            className="my-1"
          />
        );
      case "file":
        return (
          <FileUpload
            onFileSelect={(file) => {
              handleInputChange(field.name, file, "tool");
            }}
            accept={field.accept?.toString() || "*"}
            maxSize={field.maxSize || 50 * 1024 * 1024}
            value={value instanceof File ? value : null}
          />
        );
      default:
        return (
          <InputWithMagicFill
            formType="knowledge_import"
            fieldKey={field.name}
            getCurrentValues={getCurrentFormValues}
            onMagicFillApply={(val) =>
              handleInputChange(field.name, val, "tool")
            }
            metadata={getMagicFillMetadata()}
            id={field.name}
            value={
              typeof value === "string" || typeof value === "number"
                ? value
                : ""
            }
            onChange={(e) =>
              handleInputChange(field.name, e.target.value, "tool")
            }
            required={field.required}
            placeholder={field.placeholder || ""}
            className="w-full"
          />
        );
    }
  };

  const renderFormContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-full py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-sm text-muted-foreground">
            {t("knowledge.imports.loadingDetails")}
          </p>
        </div>
      );
    }

    const fileFields = toolFields.filter((f) => f.type === "file");
    const fileRelatedFields = toolFields.filter(
      (f) =>
        f.type !== "file" &&
        (f.name.toLowerCase().includes("encoding") ||
          f.name.toLowerCase().includes("delimiter") ||
          f.name.toLowerCase().includes("separator") ||
          f.name.toLowerCase().includes("charset") ||
          f.name.toLowerCase().includes("format")),
    );
    const metadataFields = toolFields.filter(
      (f) => f.type !== "file" && !fileRelatedFields.includes(f),
    );

    return (
      <div className="flex-1 overflow-y-auto">
        <form id="importForm" onSubmit={handleSubmit}>
          <div className="space-y-4 p-6">
            {/* Campos de upload primeiro */}
            {fileFields.length > 0 && (
              <div className="space-y-4">
                {fileFields.map((field) => (
                  <div key={field.name} className="space-y-2">
                    <Label htmlFor={field.name}>{field.label}</Label>
                    {renderField(field)}
                    {field.description && (
                      <p className="text-sm text-muted-foreground">
                        {field.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Campos relacionados ao arquivo (encoding, delimiter, etc.) logo após upload */}
            {fileRelatedFields.length > 0 && (
              <div className="space-y-4 pb-4 border-b">
                {fileRelatedFields.map((field) => (
                  <div key={field.name} className="space-y-2">
                    {field.type !== "boolean" && (
                      <Label htmlFor={field.name}>{field.label}</Label>
                    )}
                    {renderField(field)}
                    {field.description && (
                      <p className="text-sm text-muted-foreground">
                        {field.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Campo de nome */}
            <div className="space-y-2">
              <Label htmlFor="name">{t("knowledge.imports.fields.name")}</Label>
              <InputWithMagicFill
                formType="knowledge_import"
                fieldKey="name"
                getCurrentValues={getCurrentFormValues}
                onMagicFillApply={(val) => handleInputChange("name", val)}
                metadata={getMagicFillMetadata()}
                id="name"
                value={formData.name}
                onChange={(e) => {
                  const selectionStart = e.target.selectionStart;
                  const selectionEnd = e.target.selectionEnd;

                  handleInputChange("name", e.target.value);

                  requestAnimationFrame(() => {
                    if (nameInputRef.current) {
                      nameInputRef.current.selectionStart = selectionStart;
                      nameInputRef.current.selectionEnd = selectionEnd;
                    }
                  });
                }}
                placeholder={t("knowledge.imports.fields.namePlaceholder")}
                className="w-full"
                ref={nameInputRef}
              />
            </div>

            {/* Campo de descrição */}
            <div className="space-y-2">
              <Label htmlFor="description">
                {t("knowledge.imports.fields.description")}
              </Label>
              <TextareaWithMagicFill
                formType="knowledge_import"
                fieldKey="description"
                getCurrentValues={getCurrentFormValues}
                onMagicFillApply={(val) =>
                  handleInputChange("description", val)
                }
                metadata={getMagicFillMetadata()}
                id="description"
                value={formData.description}
                onChange={(e) => {
                  const selectionStart = e.target.selectionStart;
                  const selectionEnd = e.target.selectionEnd;

                  handleInputChange("description", e.target.value);

                  requestAnimationFrame(() => {
                    if (descInputRef.current) {
                      descInputRef.current.selectionStart = selectionStart;
                      descInputRef.current.selectionEnd = selectionEnd;
                    }
                  });
                }}
                placeholder={t(
                  "knowledge.imports.fields.descriptionPlaceholder",
                )}
                className="h-20 w-full"
                ref={descInputRef}
              />
            </div>

            {/* Outros campos de metadados */}
            {metadataFields.map((field) => (
              <div key={field.name} className="space-y-2">
                {field.type !== "boolean" && (
                  <Label htmlFor={field.name}>{field.label}</Label>
                )}
                {renderField(field)}
                {field.description && (
                  <p className="text-sm text-muted-foreground">
                    {field.description}
                  </p>
                )}
              </div>
            ))}

            {/* PBI 79319 — Facetas (Camada 2). Vai como formData.fields.facets, pipeline
                downstream propaga para metadata.facets no embedding gravado.
                `fieldKey="facets"` faz o componente consumir o MagicFillContext
                e mostrar spinner durante o magic fill, igual aos outros campos.
                Pelo menos 1 faceta (qualquer dimensão) é obrigatória — validada no handleSubmit. */}
            <div className="space-y-2">
              <Label>
                {t("facets.title")}{" "}
                <span className="text-destructive" aria-hidden>
                  *
                </span>
              </Label>
              <FacetSelector
                fieldKey="facets"
                value={
                  (formData.fields.facets as FacetFilter[] | undefined) ?? []
                }
                onChange={(next) =>
                  setFormData((prev) => ({
                    ...prev,
                    fields: { ...prev.fields, facets: next },
                  }))
                }
              />
            </div>

            {/* PBI 79328 — Metadados de negócio (opcionais, recolhidos). Magic Fill
                preenche source/subject/language/tags ao analisar o documento. */}
            <Accordion type="single" collapsible className="border rounded-md">
              <AccordionItem value="metadata" className="border-b-0">
                <AccordionTrigger className="px-4 text-sm">
                  {t("knowledge.imports.metadata.title")}
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="metadata.source">
                      {t("knowledge.imports.metadata.source")}
                    </Label>
                    <InputWithMagicFill
                      formType="knowledge_import"
                      fieldKey="metadata.source"
                      getCurrentValues={getCurrentFormValues}
                      onMagicFillApply={(val) =>
                        handleMetadataChange("source", val)
                      }
                      metadata={getMagicFillMetadata()}
                      id="metadata.source"
                      value={formData.metadata.source || ""}
                      onChange={(e) =>
                        handleMetadataChange("source", e.target.value)
                      }
                      placeholder={t(
                        "knowledge.imports.metadata.sourcePlaceholder",
                      )}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="metadata.subject">
                      {t("knowledge.imports.metadata.subject")}
                    </Label>
                    <InputWithMagicFill
                      formType="knowledge_import"
                      fieldKey="metadata.subject"
                      getCurrentValues={getCurrentFormValues}
                      onMagicFillApply={(val) =>
                        handleMetadataChange("subject", val)
                      }
                      metadata={getMagicFillMetadata()}
                      id="metadata.subject"
                      value={formData.metadata.subject || ""}
                      onChange={(e) =>
                        handleMetadataChange("subject", e.target.value)
                      }
                      placeholder={t(
                        "knowledge.imports.metadata.subjectPlaceholder",
                      )}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="metadata.language">
                      {t("knowledge.imports.metadata.language")}
                    </Label>
                    <InputWithMagicFill
                      formType="knowledge_import"
                      fieldKey="metadata.language"
                      getCurrentValues={getCurrentFormValues}
                      onMagicFillApply={(val) =>
                        handleMetadataChange("language", val)
                      }
                      metadata={getMagicFillMetadata()}
                      id="metadata.language"
                      value={formData.metadata.language || ""}
                      onChange={(e) =>
                        handleMetadataChange("language", e.target.value)
                      }
                      placeholder={t(
                        "knowledge.imports.metadata.languagePlaceholder",
                      )}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="metadata.tags">
                      {t("knowledge.imports.metadata.tags")}
                    </Label>
                    <InputWithMagicFill
                      formType="knowledge_import"
                      fieldKey="metadata.tags"
                      getCurrentValues={getCurrentFormValues}
                      onMagicFillApply={(val) =>
                        handleMetadataChange("tags", val)
                      }
                      metadata={getMagicFillMetadata()}
                      id="metadata.tags"
                      value={formData.metadata.tags || ""}
                      onChange={(e) =>
                        handleMetadataChange("tags", e.target.value)
                      }
                      placeholder={t(
                        "knowledge.imports.metadata.tagsPlaceholder",
                      )}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </form>
      </div>
    );
  };

  return (
    <MagicFillProvider>
      <div className="relative">
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent
            className="max-w-[640px] p-0 gap-0 overflow-hidden"
            style={{ width: "80%" }}
          >
            {/* Layout principal com altura fixa */}
            <div className="flex flex-col h-[600px]">
              {/* Cabeçalho fixo */}
              <div className="shrink-0 flex items-center gap-3 pl-6 pr-6 py-4 border-b">
                {tool?.logo_url && (
                  <img
                    src={tool.logo_url}
                    alt={tool.name}
                    className="h-10 w-10 object-contain flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-semibold truncate">
                    {tool?.name}
                  </h2>
                  <p className="text-sm text-muted-foreground truncate">
                    {tool?.description}
                  </p>
                </div>
              </div>

              {/* MagicFillButton oculto - apenas para auto-trigger */}
              <div className="hidden">
                <MagicFillButton
                  ref={magicFillButtonRef}
                  formType="knowledge_import"
                  getCurrentValues={getCurrentFormValues}
                  metadata={getMagicFillMetadata()}
                  onApplySuggestions={(suggested) => {
                    setFormData((prev) => {
                      const updatedFields = { ...prev.fields };
                      const updatedMetadata = { ...prev.metadata };
                      let updatedName = prev.name;
                      let updatedDescription = prev.description;

                      // PBI 79328 — o LLM pode retornar os atributos de negócio em
                      // 2 formatos: como `metadata` (objeto aninhado) ou como
                      // chaves flat `metadata.source`, `metadata.subject`, etc.
                      // Cobrimos os dois p/ não depender de como o modelo escolhe
                      // serializar; em ambos os casos populamos `formData.metadata`,
                      // que é onde o handleSubmit lê.
                      const writeMetaEntry = (k: string, v: unknown) => {
                        if (
                          typeof v === "string" ||
                          typeof v === "number" ||
                          typeof v === "boolean"
                        ) {
                          updatedMetadata[k] = String(v);
                        } else if (Array.isArray(v)) {
                          updatedMetadata[k] = v.map(String).join(", ");
                        }
                      };

                      Object.entries(suggested).forEach(([key, value]) => {
                        if (key === "name") {
                          updatedName = value as string;
                        } else if (key === "description") {
                          updatedDescription = value as string;
                        } else if (
                          key === "metadata" &&
                          value &&
                          typeof value === "object" &&
                          !Array.isArray(value)
                        ) {
                          // Forma aninhada — desempacota cada chave para formData.metadata.
                          Object.entries(
                            value as Record<string, unknown>,
                          ).forEach(([k, v]) => writeMetaEntry(k, v));
                        } else if (key.startsWith("metadata.")) {
                          // Forma flat — chave dotted: pega o sufixo depois do ponto.
                          writeMetaEntry(key.slice("metadata.".length), value);
                        } else if (key === "facets") {
                          // PBI 79319 — preserva facetas se a IA retornou vazio
                          // (vazio aqui significa "sem confiança", e o user pode
                          // já ter selecionado manualmente).
                          if (Array.isArray(value) && value.length > 0) {
                            const existing = Array.isArray(prev.fields.facets)
                              ? (prev.fields.facets as FacetFilter[])
                              : [];
                            const merged = [...existing];
                            const seen = new Set(
                              existing.map((f) => `${f.name}:${f.value}`),
                            );
                            for (const item of value as FacetFilter[]) {
                              const k = `${item.name}:${item.value}`;
                              if (!seen.has(k)) {
                                seen.add(k);
                                merged.push(item);
                              }
                            }
                            updatedFields.facets = merged;
                          }
                        } else {
                          updatedFields[key] = value;
                        }
                      });

                      return {
                        ...prev,
                        name: updatedName,
                        description: updatedDescription,
                        fields: updatedFields,
                        metadata: updatedMetadata,
                      };
                    });
                  }}
                />
              </div>

              {/* Área de scroll apenas para o formulário */}
              <div className="flex-1 overflow-y-auto">
                {renderFormContent()}
              </div>

              {/* Rodapé fixo */}
              <div className="shrink-0 border-t bg-accent p-4 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={onCancel}>
                  {t("knowledge.common.cancel")}
                </Button>
                <Button type="submit" form="importForm">
                  {t("knowledge.imports.add")}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </MagicFillProvider>
  );
}
